/**
 * Semantic color layer — intent-named tokens that point at a primitive (either
 * a ramp + shade, or a standalone color). This is the framework-agnostic
 * contract the rest of the system reads; it deliberately does NOT import any UI
 * framework (the semantic → 10-shade Mantine adapter is the frontend's job:
 * frontend/src/theme/adapter.ts).
 *
 * Light-mode only by design: the TEO product has no dark mode, so there are no
 * scheme-aware / dark-variant tokens — every token resolves to a single
 * light-mode value (a settled product fact, not a temporary deferral).
 */
import { commonColors, primitiveRamps, } from './primitives.js';
/** Build a ramp-backed {@link SemanticColor}, resolving the hex at authoring time. */
function ref(ramp, shade) {
    return { kind: 'ramp', ramp, shade, value: primitiveRamps[ramp][shade] };
}
/** Build a standalone-color {@link SemanticColor}. */
function common(primitive) {
    return { kind: 'common', primitive, value: commonColors[primitive] };
}
/**
 * Resolve any {@link SemanticColor} back to its underlying CSS value through its
 * primitive source — a 6-digit hex for ramp colors, or the literal `transparent`
 * for that common color. Useful for consumers (and tests) that want to assert the
 * semantic layer never drifts from the primitives it claims to reference.
 */
export function resolveSemanticColor(token) {
    switch (token.kind) {
        case 'ramp':
            return primitiveRamps[token.ramp][token.shade];
        case 'common':
            return commonColors[token.primitive];
        default: {
            const _exhaustive = token;
            throw new Error(`Unhandled semantic color kind: ${JSON.stringify(_exhaustive)}`);
        }
    }
}
/**
 * Intent → primitive mapping. `info` anchors on the dedicated blue ramp
 * (§8 of the color system) — informational emphasis gets its own hue
 * so it no longer reads as a second primary. blue[6] is the lightest filled base
 * that keeps white text ≥ 4.5:1 (4.62:1, just past AA — blue[5] is 3.95:1), so
 * the anchor must not lighten.
 */
export const intent = {
    primary: ref('purple', 7),
    danger: ref('red', 6),
    success: ref('green', 6),
    warning: ref('orange', 6),
    info: ref('blue', 6),
    neutral: ref('neutral', 6),
};
/**
 * Surface (background) tokens for layered UI.
 *
 * `selected` and `hover` are the card-surface additions.
 *
 * ⚠️ `selected` (purple[0]) is only 1.19:1 against white and is NOT capable of
 * carrying selection on its own — a tint pale enough to keep body copy legible
 * is far too pale to register as a state change. It is always paired with
 * `border.selected` at 2px plus a non-colour mark; the border is what satisfies
 * 1.4.11, the fill is reinforcement. Darkening it to make the fill sufficient
 * would push text below AA, which is the trade the ruling declined.
 *
 * ⚠️ `hover` resolves to the same value as `page` (neutral[0]). Kept as a
 * distinct name for the same reason `text.muted` and `text.secondary` are: the
 * role is different, and when a hover treatment needs its own value the change
 * happens here rather than at every call site.
 */
export const surface = {
    page: ref('neutral', 0),
    default: common('white'),
    raised: common('white'),
    sunken: ref('neutral', 1),
    inverse: ref('purple', 7),
    selected: ref('purple', 0),
    hover: ref('neutral', 0),
};
/**
 * Foreground / text tokens.
 *
 * `muted` was the sub-AA carry-over flagged in PR #391 (neutral[6] #868e96,
 * ≈3.3:1). The SegmentedFilter build settled it: the meta lines that use it
 * render at ~12px, far under the 18.66px large-text threshold, so the
 * large-text exemption never
 * applied. neutral[6] survives as the disabled floor (see `border.strong`).
 *
 * ⚠️ `muted` and `secondary` therefore resolve to the SAME value (neutral[7]).
 * That is deliberate but not ideal, and it is worth understanding before either
 * is changed. The neutral ramp has no step between neutral[6] (3.32:1, fails)
 * and neutral[7] (8.18:1, passes), so there is no third value that is both
 * lighter than `secondary` and legible — the ramp cannot express the hierarchy
 * level `muted` was trying to occupy. Until a step is added between them, that
 * level is carried by weight and size rather than colour.
 *
 * The two names are kept distinct rather than aliased so call sites keep saying
 * which level they *mean*: when the ramp gains an intermediate step, `muted`
 * moves to it and every consumer follows without a rename. The companion test
 * pins the collapse so it reads as a decision rather than a copy-paste slip.
 *
 * ⚠️ `link` (orange[9] #cc6600) is still ≈3.8:1 — AA large-text only. It matches
 * the existing `Anchor c="orange.9"` brand link color, so raising it is a brand
 * decision rather than an accessibility fix we can make unilaterally. The
 * companion test pins its ratio so it cannot silently get worse.
 */
export const text = {
    primary: ref('purple', 7),
    secondary: ref('neutral', 7),
    muted: ref('neutral', 7),
    inverse: common('white'),
    link: ref('orange', 9),
    onBand: common('white'),
};
/**
 * Border / divider tokens.
 *
 * `default` and `subtle` are decorative — they outline cards, inputs and other
 * shapes that already carry their own affordance, so they sit below the 3:1 of
 * WCAG 1.4.11 by design. `strong` is the one that has to carry a *meaningful*
 * boundary (the divider between interactive segments, where the line is the only
 * thing saying "these are separate controls"), so it resolves to neutral[6] —
 * the lightest step in the ramp that clears 3:1 on white. neutral[5] is 2.07:1.
 *
 * `interactive` and `selected` are the card-surface additions.
 * They resolve to the same values as `strong` and `focus` respectively, and are
 * kept as separate names because they answer different questions — "is this
 * clickable" and "is this chosen" are not "is this a divider" or "is this
 * focused", and a future divergence should not require touching every consumer.
 *
 * ⚠️ `interactive` is exactly 2.99:1 against `surface.sunken`. That single
 * hundredth is why a sunken surface is modelled as a well — borderless and
 * never interactive — rather than an item: the alternative was darkening the
 * boundary until the number passed, which would have made every interactive
 * card on white heavier to rescue a variant that had no honest use. Pinned by
 * a test so the exclusion cannot be quietly dropped.
 */
export const border = {
    default: ref('neutral', 3),
    subtle: ref('neutral', 2),
    strong: ref('neutral', 6),
    focus: ref('purple', 7),
    interactive: ref('neutral', 6),
    selected: ref('purple', 7),
};
/**
 * On-color foregrounds — the text/icon color to place *on top of* a solid
 * intent-anchor fill (the intent's `[6]`/`[7]` shade). Each is the
 * WCAG-AA-passing companion to its intent, computed against the actual anchor:
 *
 *   intent fill   on-color   contrast   why
 *   primary  #470047  white    15.45:1   ✓
 *   danger   #b63333  white     5.98:1   ✓ (ink would be 2.58:1 ✗)
 *   success  #607d12  white     4.73:1   ✓ (ink would be 3.27:1 ✗)
 *   warning  #ffb94e  ink       9.05:1   ✓ — white is only 1.71:1 ✗, so on-warning
 *                                          MUST be ink (the brand purple #470047).
 *   info     #4675be  white     4.62:1   ✓ — knife-edge past AA (blue[6]); if the
 *                                          anchor ever lightens, onInfo must flip.
 *   neutral  #868e96  ink       4.65:1   ✓ — white is only 3.32:1 ✗, so on-neutral
 *                                          is ink too.
 *
 * Note the `neutral` row is `#868e96` used as a FILL with ink text on top. The
 * same hex is only 3.32:1 as TEXT on white, which is why `text.muted` no longer
 * resolves to it — it is fine as a fill here and sub-AA as body copy. neutral[6]
 * survives in that text direction only as the disabled floor, where the contrast
 * minimum is formally waived.
 *
 * `ink` is the brand foreground (`commonColors.ink` = `purple[7]` = #470047).
 */
export const onColor = {
    onPrimary: common('white'),
    onDanger: common('white'),
    onSuccess: common('white'),
    onWarning: ref('purple', 7),
    onInfo: common('white'),
    onNeutral: ref('purple', 7),
};
/**
 * Interaction-state colors per intent — the resting/hover/active/disabled fills
 * a control cycles through. Derived purely by stepping along the intent's ramp:
 * hover/active go one/two shades darker than `base`, disabled goes lighter.
 *
 * This is the concrete reason `danger` got a real 10-shade red ramp:
 * the old flat `#b63333` ×10 fill could not express these states (a
 * filled-red control had no hover/active delta). For `base`, `primary` anchors on
 * `purple[7]` (the brand foreground shade — `text.primary`, `theme.black`); every
 * other intent — info included since its re-anchor onto the blue ramp —
 * anchors on its `[6]`, matching Mantine's filled-variant base.
 *
 * NOTE (Phase-2): Mantine's own filled `<Button color="purple">` resting fill is
 * `purple[6]` (#800080), because `theme.primaryShade.light` defaults to 6 and
 * `theme.ts` does not override it. So `interactionState.primary.base` (purple[7])
 * is the BRAND anchor, not Mantine's filled-button base — a Phase-2 component that
 * wants to match a built-in filled purple Button must either consume `[6]` or the
 * theme set `primaryShade: 7` (a deliberate visual change, out of the adapter's
 * strict-parity scope). Subtlety to watch when applying that fix: Mantine derives
 * filled-hover as `shade + 1`, so today's filled-purple HOVER is `purple[7]`
 * (#470047 = brand ink) — it *coincidentally* matches the brand color. Setting
 * `primaryShade: 7` shifts the hover to `purple[8]` (#330033, much darker), so
 * the visual review for that change must check hover, not just the resting fill.
 */
export const interactionState = {
    primary: {
        base: ref('purple', 7),
        hover: ref('purple', 8),
        active: ref('purple', 9),
        disabled: ref('purple', 3),
    },
    danger: {
        base: ref('red', 6),
        hover: ref('red', 7),
        active: ref('red', 8),
        disabled: ref('red', 3),
    },
    success: {
        base: ref('green', 6),
        hover: ref('green', 7),
        active: ref('green', 8),
        disabled: ref('green', 3),
    },
    warning: {
        base: ref('orange', 6),
        hover: ref('orange', 7),
        active: ref('orange', 8),
        disabled: ref('orange', 3),
    },
    info: {
        base: ref('blue', 6),
        hover: ref('blue', 7),
        active: ref('blue', 8),
        disabled: ref('blue', 3),
    },
    neutral: {
        base: ref('neutral', 6),
        hover: ref('neutral', 7),
        active: ref('neutral', 8),
        disabled: ref('neutral', 3),
    },
};
/**
 * Intent tint triads — the soft `{ bg, fg, border }` set a low-emphasis intent
 * surface (InlineAlert, StatusBadge, light AppButton) is built from: a pale
 * shade-0 background, a readable dark foreground, and a boundary border.
 *
 * `fg` is shade-8 (not the [6]/[7] anchor) so the text clears WCAG AA on the
 * pale shade-0 bg for every intent that can — except `warning`, where NO orange
 * shade reaches AA on orange[0] (orange[9]/orange[0] is only 3.64:1), so the
 * warning tint foreground is `ink` (#470047 on #fff8eb = 14.63:1), mirroring the
 * on-warning decision above. Verified fg-on-bg contrast:
 *   primary 14.9 · danger 9.68 · success 8.76 · warning 14.63 (ink) · info 8.94 (blue) · neutral 10.91 — all ≥ AA.
 *
 * `border` is the sole boundary cue for these low-emphasis surfaces, so it must
 * clear WCAG 1.4.11 (≥ 3:1, non-text UI) against three grounds it can be drawn on:
 * the tint bg, the page surface (neutral[0]), and white (StatusBadge sits on white
 * cards). Enforced by a test — see tokens.test.ts.
 *
 * `warning.border` was the last one under 3:1. The designer's dialog-actions ruling
 * settled the open hue question in favour of the functional-amber family: amber[7]
 * #b45309, 4.75:1 on the warning wash. Fixed here at the token layer, so every
 * alert and tint surface in the app inherits the repair rather than each one
 * patching its own border. The border test no longer skips warning.
 */
export const intentTint = {
    primary: { bg: ref('purple', 0), fg: ref('purple', 8), border: ref('purple', 4) },
    danger: { bg: ref('red', 0), fg: ref('red', 8), border: ref('red', 4) },
    success: { bg: ref('green', 0), fg: ref('green', 8), border: ref('green', 6) },
    warning: { bg: ref('orange', 0), fg: ref('purple', 7), border: ref('amber', 7) },
    info: { bg: ref('blue', 0), fg: ref('blue', 8), border: ref('blue', 4) },
    neutral: { bg: ref('neutral', 0), fg: ref('neutral', 8), border: ref('neutral', 6) },
};
export const intentIndicator = {
    danger: { edge: ref('red', 6), icon: ref('red', 6) },
    success: { edge: ref('green', 6), icon: ref('green', 7) },
    warning: { edge: ref('amber', 7), icon: ref('amber', 7) },
    info: { edge: ref('blue', 6), icon: ref('blue', 6) },
    neutral: { edge: ref('neutral', 6), icon: ref('neutral', 7) },
};
/** Filled-button background across resting / hover / active (the interactive fill). */
export const buttonFilledBackgroundState = {
    primary: { base: ref('purple', 7), hover: ref('purple', 8), active: ref('purple', 9) },
    danger: { base: ref('red', 6), hover: ref('red', 7), active: ref('red', 8) },
    success: { base: ref('green', 6), hover: ref('green', 7), active: ref('green', 8) },
    warning: { base: ref('amber', 3), hover: ref('amber', 4), active: ref('amber', 5) },
    info: { base: ref('blue', 6), hover: ref('blue', 7), active: ref('blue', 8) },
    neutral: { base: ref('neutral', 2), hover: ref('neutral', 3), active: ref('neutral', 4) },
};
export const buttonColor = {
    filled: {
        primary: { bg: ref('purple', 7), fg: common('white'), border: ref('purple', 7) },
        danger: { bg: ref('red', 6), fg: common('white'), border: ref('red', 6) },
        success: { bg: ref('green', 6), fg: common('white'), border: ref('green', 6) },
        warning: { bg: ref('amber', 3), fg: ref('purple', 7), border: ref('amber', 3) },
        info: { bg: ref('blue', 6), fg: common('white'), border: ref('blue', 6) },
        neutral: { bg: ref('neutral', 2), fg: ref('neutral', 9), border: ref('neutral', 4) },
    },
    outline: {
        primary: { bg: common('white'), fg: ref('purple', 8), border: ref('purple', 7) },
        danger: { bg: common('white'), fg: ref('red', 8), border: ref('red', 6) },
        success: { bg: common('white'), fg: ref('green', 8), border: ref('green', 6) },
        warning: { bg: common('white'), fg: ref('amber', 8), border: ref('amber', 7) },
        info: { bg: common('white'), fg: ref('blue', 8), border: ref('blue', 6) },
        neutral: { bg: common('white'), fg: ref('neutral', 8), border: ref('neutral', 6) },
    },
    light: {
        primary: { bg: ref('purple', 0), fg: ref('purple', 8), border: ref('purple', 2) },
        danger: { bg: ref('red', 0), fg: ref('red', 8), border: ref('red', 2) },
        success: { bg: ref('green', 0), fg: ref('green', 8), border: ref('green', 2) },
        warning: { bg: ref('amber', 1), fg: ref('amber', 9), border: ref('amber', 2) },
        info: { bg: ref('blue', 0), fg: ref('blue', 8), border: ref('blue', 2) },
        neutral: { bg: ref('neutral', 1), fg: ref('neutral', 8), border: ref('neutral', 3) },
    },
    subtle: {
        primary: { bg: common('transparent'), fg: ref('purple', 8), border: common('transparent') },
        danger: { bg: common('transparent'), fg: ref('red', 8), border: common('transparent') },
        success: { bg: common('transparent'), fg: ref('green', 8), border: common('transparent') },
        warning: { bg: common('transparent'), fg: ref('amber', 8), border: common('transparent') },
        info: { bg: common('transparent'), fg: ref('blue', 8), border: common('transparent') },
        neutral: { bg: common('transparent'), fg: ref('neutral', 8), border: common('transparent') },
    },
};
/**
 * Shared disabled treatment across all intents; contrast-exempt by WCAG. This
 * documents what Mantine's built-in disabled selector renders (its own
 * `--mantine-color-disabled` / `-color` vars, with a transparent border), so the
 * published token contract matches the on-screen component rather than describing a
 * treatment the button would have to fight Mantine's selector to apply.
 */
export const buttonDisabled = {
    bg: ref('neutral', 2),
    fg: ref('neutral', 5),
    border: common('transparent'),
};
/** Resolve a button's `{ bg, fg, border }` from its variant + intent. */
export function resolveButtonColors(variant, intent) {
    return buttonColor[variant][intent];
}
/**
 * The full semantic color namespace. Grouped here so the barrel can re-export a
 * single `color` object whose shape mirrors the intended `color.intent.*`,
 * `color.surface.*`, `color.text.*`, `color.border.*`, `color.onColor.*`,
 * `color.interactionState.*`, `color.intentTint.*` token paths.
 */
export const color = {
    intent,
    surface,
    text,
    border,
    onColor,
    interactionState,
    intentTint,
    intentIndicator,
    buttonColor,
    buttonFilledBackgroundState,
    buttonDisabled,
};
